/**
 * Reads the tables of a WOFF 2.0 file (W3C WOFF 2.0, §"WOFF2 Header", §"Table directory" and
 * §"Compressed data format"): one Brotli stream holding every table, end to end. A transformed
 * `glyf` and `loca` are rebuilt (`woff2Glyf.ts`), and so is a transformed `hmtx` (`woff2Hmtx.ts`).
 */
import { ByteReader } from '../../../../resources/formats/binaryresource/byteReader';
import { exactly } from '../../../../resources/formats/binaryresource/compressedStream';
import { perObject } from '../../../../utils/perObject';
import { loadBrotliDecoder } from './brotliDecoder';
import { SCENE_FONT_TABLE_TAGS, sfntFileOfTables } from './sfntTables';
import { rebuildGlyfTables } from './woff2Glyf';
import { rebuiltHmtxOf } from './woff2Hmtx';
export const WOFF2_SIGNATURE = 0x774f4632; // 'wOF2'
const SFNT_COLLECTION_FLAVOR = 0x74746366; // 'ttcf'
const WOFF2_HEADER_SIZE = 48;
const FLAVOR_OFFSET = 4;
const NUM_TABLES_OFFSET = 12;
const TOTAL_COMPRESSED_SIZE_OFFSET = 20;
// Table directory flags byte: tag index in bits 0-5, transform version in bits 6-7.
const TAG_INDEX_MASK = 0x3f;
const TRANSFORM_VERSION_SHIFT = 6;
const ARBITRARY_TAG_INDEX = 63; // the tag follows as four bytes
const TAG_SIZE = 4;
/** The "Known Table Tags" of the WOFF2 spec, by tag index. */
const KNOWN_TAGS = [
    'cmap',
    'head',
    'hhea',
    'hmtx',
    'maxp',
    'name',
    'OS/2',
    'post',
    'cvt ',
    'fpgm',
    'glyf',
    'loca',
    'prep',
    'CFF ',
    'VORG',
    'EBDT',
    'EBLC',
    'gasp',
    'hdmx',
    'kern',
    'LTSH',
    'PCLT',
    'VDMX',
    'vhea',
    'vmtx',
    'BASE',
    'GDEF',
    'GPOS',
    'GSUB',
    'EBSC',
    'JSTF',
    'MATH',
    'CBDT',
    'CBLC',
    'COLR',
    'CPAL',
    'SVG ',
    'sbix',
    'acnt',
    'avar',
    'bdat',
    'bloc',
    'bsln',
    'cvar',
    'fdsc',
    'feat',
    'fmtx',
    'fvar',
    'gvar',
    'hsty',
    'just',
    'lcar',
    'mort',
    'morx',
    'opbd',
    'prop',
    'trak',
    'Zapf',
    'Silf',
    'Glat',
    'Gloc',
    'Feat',
    'Sill',
];
/** `UIntBase128`: seven bits per byte, high bit set while more follow, at most five bytes and 32 bits. */
const BASE128_MAX_BYTES = 5;
const BASE128_OVERFLOW_LIMIT = 0x01ffffff;
function readBase128(reader) {
    let accumulated = 0;
    for (let index = 0; index < BASE128_MAX_BYTES; index++) {
        const byte = reader.u8();
        if (index === 0 && byte === 0x80)
            throw new Error('expected a UIntBase128 with no leading zero');
        if (accumulated > BASE128_OVERFLOW_LIMIT)
            throw new Error('expected a UIntBase128 within 32 bits');
        accumulated = accumulated * 128 + (byte & 0x7f);
        if ((byte & 0x80) === 0)
            return accumulated;
    }
    throw new Error(`expected a UIntBase128 of at most ${BASE128_MAX_BYTES} bytes`);
}
/** `glyf` and `loca` are transformed at version 0 and plain at version 3; every other table is plain at version 0. */
function isTransformedTable(tag, version) {
    return tag === 'glyf' || tag === 'loca' ? version === 0 : version !== 0;
}
function readDirectoryEntry(reader) {
    const flags = reader.u8();
    const tagIndex = flags & TAG_INDEX_MASK;
    const tag = tagIndex === ARBITRARY_TAG_INDEX ? String.fromCharCode(...reader.take(TAG_SIZE)) : KNOWN_TAGS[tagIndex];
    if (tag === undefined)
        throw new RangeError(`expected a known table tag index, got ${tagIndex}`);
    const isTransformed = isTransformedTable(tag, flags >> TRANSFORM_VERSION_SHIFT);
    const originalLength = readBase128(reader);
    return { tag, isTransformed, storedLength: isTransformed ? readBase128(reader) : originalLength };
}
/**
 * The directory and the decoded stream of one buffer. The metrics and the outlines of a scene font
 * read the same buffer, which Brotli decodes once. A buffer that does not decode keeps its rejection.
 */
const decodedStreamOf = perObject(async (bytes) => {
    const dv = new DataView(bytes);
    if (dv.getUint32(FLAVOR_OFFSET, false) === SFNT_COLLECTION_FLAVOR) {
        throw new Error('expected a single-font .woff2, got a font collection');
    }
    const reader = ByteReader.bigEndian(new Uint8Array(bytes));
    reader.seek(WOFF2_HEADER_SIZE);
    const entries = Array.from({ length: dv.getUint16(NUM_TABLES_OFFSET, false) }, () => readDirectoryEntry(reader));
    const decodedLength = entries.reduce((sum, entry) => sum + entry.storedLength, 0);
    const compressed = reader.take(dv.getUint32(TOTAL_COMPRESSED_SIZE_OFFSET, false));
    const decode = await loadBrotliDecoder();
    return { entries, decoded: exactly(decodedLength, decode(compressed, decodedLength)) };
});
/** The bytes each table takes in the decoded stream, by tag. */
function storedTablesOf({ entries, decoded }) {
    const stored = new Map();
    let offset = 0;
    for (const { tag, storedLength, isTransformed } of entries) {
        stored.set(tag, { bytes: decoded.subarray(offset, offset + storedLength), offset, isTransformed });
        offset += storedLength;
    }
    return stored;
}
/** The tables of `stored` that the stream holds plain, of those `tags` names. */
function plainTablesOf(stored, tags) {
    const tables = new Map();
    for (const [tag, { bytes, isTransformed }] of stored)
        if (!isTransformed && tags.includes(tag))
            tables.set(tag, bytes);
    return tables;
}
/**
 * Throws on a collection, a directory or stream that does not fit `bytes`, a Brotli stream that
 * does not decode to the lengths the directory sums to, or a transformed table FreeType rejects.
 */
export async function readWoff2Tables(bytes, tags = SCENE_FONT_TABLE_TAGS) {
    const stream = await decodedStreamOf(bytes);
    const stored = storedTablesOf(stream);
    const tables = plainTablesOf(stored, tags);
    const isGlyfWanted = tags.includes('glyf');
    const isHmtxRebuilt = stored.get('hmtx')?.isTransformed === true && tags.includes('hmtx');
    const glyf = stored.get('glyf');
    const rebuilt = glyf?.isTransformed && (isGlyfWanted || isHmtxRebuilt) ? rebuildGlyfTables(glyf.bytes) : null;
    if (rebuilt && isGlyfWanted) {
        tables.set('glyf', rebuilt.glyf);
        tables.set('loca', rebuilt.loca);
    }
    if (isHmtxRebuilt)
        tables.set('hmtx', rebuiltHmtxOf(stream.decoded, stored, rebuilt?.xMins ?? null));
    return sfntFileOfTables(tables);
}
//# sourceMappingURL=woff2Container.js.map